import { useMemo, useState } from 'react';
import { FolderOpen, Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { useCollections } from '../../hooks/useCollections';
import { truncateMiddle } from '../../lib/documentName';
import type { CollectionStats } from '../../types';
import { CollectionHealthDot } from '../collections/CollectionHealthDot';
import { CollectionRowMenu } from '../collections/CollectionRowMenu';
import { DeleteCollectionDialog } from '../collections/DeleteCollectionDialog';
import { EmptyState } from '../EmptyState';

interface Props {
  selected: string | null;
  onSelect: (id: string) => void;
  onCreateClick: () => void;
}

/** Navegação lateral estilo Drive: lista de coleções ("pastas"), busca e criação. */
export function Sidebar({ selected, onSelect, onCreateClick }: Props) {
  const { data: collections, isLoading } = useCollections();
  const [toDelete, setToDelete] = useState<CollectionStats | null>(null);
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    if (!collections) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return collections;
    return collections.filter(
      (c) => c.collection_id.toLowerCase().includes(q) || c.description.toLowerCase().includes(q),
    );
  }, [collections, filter]);

  return (
    <div className="flex h-full flex-col">
      {/* Mesmo gutter (px-4) e ritmo vertical (py-3) do TopBar — os dois têm
          botão `size="sm"`, então as alturas casam sem número mágico. */}
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <span className="text-sm font-semibold">Coleções</span>
        <Button size="sm" variant="ghost" onClick={onCreateClick} aria-label="Nova coleção">
          <Plus className="size-4" />
        </Button>
      </div>

      {!isLoading && collections && collections.length > 0 && (
        <div className="shrink-0 px-4 py-3">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar coleção…"
              className="h-8 pl-8 text-sm"
              aria-label="Buscar coleção"
            />
          </div>
        </div>
      )}

      {/* `min-h-0` é obrigatório: filho de flex column tem `min-height: auto`,
          então sem isso o ScrollArea cresce até caber toda a lista em vez de
          rolar — e o `overflow-hidden` do <aside> corta o excedente. O root do
          ScrollArea é só `relative`, não tem overflow próprio pra anular o
          min-height. */}
      <ScrollArea className="min-h-0 flex-1">
        {isLoading && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        )}

        {!isLoading && !collections?.length && (
          <EmptyState
            icon={<FolderOpen />}
            title="Sem coleções"
            description="Crie uma coleção para começar a indexar documentos."
            action={
              <Button size="sm" onClick={onCreateClick}>
                <Plus className="mr-1 size-4" />
                Nova coleção
              </Button>
            }
          />
        )}

        {!isLoading && collections && collections.length > 0 && filtered.length === 0 && (
          <p className="text-muted-foreground px-4 py-6 text-center text-xs break-words">
            {/* Termo truncado: a sidebar tem largura fixa, e o texto cru de uma
                busca longa vazava para fora dela. */}
            Nenhuma coleção corresponde a "{truncateMiddle(filter, 30)}".
          </p>
        )}

        {filtered.map((col) => {
          const isSelected = selected === col.collection_id;
          return (
            // Estrutura do SidebarMenuItem do shadcn: o item é o contexto
            // posicionado, o botão ocupa a linha inteira e a ação flutua sobre
            // ele à direita. Enquanto a ação era irmã no flex, ela disputava
            // espaço com o nome da coleção e saía da sidebar quando o nome
            // crescia. O `pr-10` no botão reserva o lugar dela.
            <div
              key={col.collection_id}
              className={cn(
                'group relative border-l-2 border-transparent transition-colors',
                'hover:bg-accent',
                isSelected && 'border-l-primary bg-accent',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(col.collection_id)}
                aria-current={isSelected ? 'true' : undefined}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 py-2.5 pr-10 pl-4 text-left text-sm',
                  isSelected && 'font-medium',
                )}
              >
                <CollectionHealthDot collection={col} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {/* Corta no meio, não no fim: id longo costuma se diferenciar
                      pelo sufixo (ano, versão), e cortando no fim duas coleções
                      diferentes viravam a mesma coisa na lista. O nome inteiro
                      fica no hover, e o truncate por CSS segura o que sobrar. */}
                  <span className="truncate" title={col.collection_id}>
                    {truncateMiddle(col.collection_id, 28)}
                  </span>
                  <span className="text-muted-foreground truncate text-xs">
                    {col.indexed}/{col.total_documents} indexados · {col.total_chunks} chunks
                    {col.failed > 0 && (
                      <span className="text-destructive"> · {col.failed} falha(s)</span>
                    )}
                  </span>
                </span>
              </button>

              <div className="absolute top-1/2 right-1 -translate-y-1/2">
                <CollectionRowMenu collection={col} onRequestDelete={setToDelete} />
              </div>
            </div>
          );
        })}
      </ScrollArea>

      <DeleteCollectionDialog
        collection={toDelete}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        onDeleted={() => {
          if (toDelete?.collection_id === selected) onSelect('');
        }}
      />
    </div>
  );
}
